import { Link, useLocation } from 'react-router-dom';
import PhosphorIcon from './ui/PhosphorIcon';
import BrandLogo from './ui/BrandLogo';
import { contact, footerLinks, social, positioning } from '../content/siteContent';

const linkClass = 'text-sm transition-colors';

function resolveHashHref(hash, pathname) {
  if (pathname === '/') return hash;

  if (pathname === '/fleet-saas' && hash === '#cities') return '#coverage';

  if (pathname === '/logistics' || pathname === '/fleet-saas') {
    if (hash === '#cities' || hash === '#faq' || hash === '#contact') return hash;
  }

  return `/${hash}`;
}

function FooterLink({ link }) {
  const location = useLocation();

  if (link.external && link.href) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {link.label}
      </a>
    );
  }

  if (link.to) {
    return (
      <Link to={link.to} className={linkClass}>
        {link.label}
      </Link>
    );
  }

  if (link.hash) {
    const href = resolveHashHref(link.hash, location.pathname);
    return (
      <a href={href} className={linkClass}>
        {link.label}
      </a>
    );
  }

  if (link.href) {
    return (
      <a href={link.href} className={linkClass}>
        {link.label}
      </a>
    );
  }

  return null;
}

export default function Footer() {
  return (
    <footer className="relative" style={{ padding: '0 16px 28px' }}>
      <div className="glass relative z-10" style={{ maxWidth: 1192, margin: '0 auto', borderRadius: 32, padding: 'clamp(24px, 4vw, 40px)' }}>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-10">
          <div className="col-span-2">
            <BrandLogo asLink imageClassName="h-10 w-auto object-contain" className="mb-4" />
            <p className="text-sm leading-relaxed mb-5 max-w-xs text-slate-600">{positioning.tagline}</p>
            <div className="flex gap-2">
              {[
                { icon: 'TwitterLogo', href: social.twitter, label: 'Twitter' },
                { icon: 'FacebookLogo', href: social.facebook, label: 'Facebook' },
                { icon: 'InstagramLogo', href: social.instagram, label: 'Instagram' },
                { icon: 'LinkedinLogo', href: social.linkedin, label: 'LinkedIn' },
              ].map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={item.label}
                  className="w-9 h-9 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center text-slate-600 hover:text-brand-blue transition-colors"
                >
                  <PhosphorIcon name={item.icon} size={18} weight="fill" />
                </a>
              ))}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1">
                Logistics: 4 cities
              </span>
              <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1">
                Fleet SaaS: Pan-India
              </span>
            </div>
          </div>

          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h3 className="text-slate-900 font-semibold text-sm mb-3">{title}</h3>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row justify-between gap-3 text-xs text-slate-600">
          <p>
            &copy; {new Date().getFullYear()} 5INCH VENTURES PRIVATE LIMITED. FastWhistle is a product of 5INCH
            VENTURES PRIVATE LIMITED.
          </p>
          <p>
            <a href={`tel:${contact.phoneRaw}`} className="hover:text-brand-blue transition-colors">
              {contact.phone}
            </a>
            {' · '}
            <a href={`mailto:${contact.email}`} className="hover:text-brand-blue transition-colors">
              {contact.email}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
